import React, { useEffect, useRef, useState } from "react";

import {
  isNullOrUndefined,
  isValidNonnegativeNumber,
  isWithinBounds,
  useGetFootprintingDairyHerdDetails,
  useMutateFootprintingDairyHerdDetails,
  useTranslation,
} from "utils";

import { Card, Flex, Form, FormInstance, InputNumber, PageSpin } from "components";

interface DairyGeneralProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

export const HerdDetailsForm = ({ primaryDataId, readOnly, farmId, year }: DairyGeneralProps) => {
  const { t } = useTranslation("footprintingDairy");
  const formRef = useRef<FormInstance>(null);

  const [avgCowLosses, setAvgCowLosses] = useState<number | null>(null);
  const [avgCowUsefulLifeMonths, setAvgCowUsefulLifeMonths] = useState<number | null>(null);

  const { data: herdDetails, isSuccess, isLoading } = useGetFootprintingDairyHerdDetails(primaryDataId ?? "", { enabled: !!primaryDataId });

  const { mutate: mutateHerdDetails } = useMutateFootprintingDairyHerdDetails(
    farmId,
    year,
    {
      avgCowLosses,
      avgCowUsefulLifeMonths,
    },
    primaryDataId
  );

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateHerdDetails();
      }
    },
    [isLoading, mutateHerdDetails, readOnly]
  );

  useEffect(() => {
    if (herdDetails && isSuccess) {
      setAvgCowLosses(herdDetails.avgCowLosses ?? null);
      setAvgCowUsefulLifeMonths(herdDetails.avgCowUsefulLifeMonths ?? null);

      formRef.current?.setFieldsValue({
        avgCowLosses: herdDetails.avgCowLosses ?? null,
        avgCowUsefulLifeMonths: herdDetails.avgCowUsefulLifeMonths ?? null,
      });

      const fieldsToValidate = (["avgCowLosses", "avgCowUsefulLifeMonths"] as const).some(
        (field) => herdDetails[field] !== null && herdDetails[field] !== undefined
      );

      if (fieldsToValidate && formRef.current) {
        formRef.current?.validateFields();
      }
    }
  }, [herdDetails, isSuccess]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <div>
      <h1 className="center margin-bottom-200 padding-left-300 padding-right-300 margin-left-100 margin-right-100">{t("dairyHerdDetailsForm.title")}</h1>
      <Form ref={formRef}>
        <Card className="padding-200 margin-top-100">
          <Flex vertical>
            <Flex vertical gap={8}>
              <h4>{t("dairyHerdDetailsForm.cowLostQuestion")}</h4>
              <p className="margin-top-0 margin-bottom-150 grey-600-text paragraph-m">{t("dairyHerdDetailsForm.cowLostQuestionDescription")}</p>
            </Flex>
            <Form.Item>
              <InputNumber
                disabled={readOnly}
                name="avgCowLosses"
                dataTestId="footprinting-herd-cow-losses-input"
                label={t("dairyHerdDetailsForm.question.avgCowLossesInput")}
                suffix={t("dairyHerdDetailsForm.question.avgCowLossesSuffix")}
                value={avgCowLosses}
                initialValue={avgCowLosses}
                onChange={(v) => setAvgCowLosses(v)}
                validateFirst
                rules={[
                  { required: true, type: "number", message: t("dairyHerdDetailsForm.error.validNumberCowLoss") },
                  {
                    validator: (_: unknown, v: number) => {
                      if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                        return Promise.reject(new Error(t("dairyHerdDetailsForm.error.validNumberCowLoss")));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />
            </Form.Item>
          </Flex>
        </Card>

        <Card className="padding-200 margin-top-100">
          <Flex vertical>
            <Flex vertical gap={8}>
              <h4>{t("dairyHerdDetailsForm.avgCowUsefulLifeMonthsQuestion")}</h4>
              <p className="margin-top-0 margin-bottom-150 grey-600-text paragraph-m">{t("dairyHerdDetailsForm.avgCowUsefulLifeMonthsQuestionDescription")}</p>
            </Flex>
            <Form.Item>
              <InputNumber
                disabled={readOnly}
                name="avgCowUsefulLifeMonths"
                dataTestId="footprinting-herd-cow-useful-life-input"
                label={t("dairyHerdDetailsForm.question.avgCowUsefulLifeMonthsInput")}
                suffix={t("dairyHerdDetailsForm.question.avgCowUsefulLifeMonthsSuffix")}
                value={avgCowUsefulLifeMonths}
                initialValue={avgCowUsefulLifeMonths}
                onChange={(v) => setAvgCowUsefulLifeMonths(v)}
                validateFirst
                rules={[
                  { required: true, type: "number", message: t("dairyHerdDetailsForm.error.validNumberMonths") },
                  {
                    validator: (_: unknown, v: number) => {
                      if (isNullOrUndefined(v) || !isWithinBounds(v, 20, 150)) {
                        return Promise.reject(new Error(t("dairyHerdDetailsForm.error.validNumberMonths")));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />
            </Form.Item>
          </Flex>
        </Card>
      </Form>
    </div>
  );
};
